import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type ColumnDef,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import {
  reportKeys,
  useCreateReportVersion,
  useReportById,
  useReportSummaries,
} from "@/hooks/useReportQueries";
import { handleApiError, isNotFoundError } from "@/hooks/useApiQueries";
import {
  Button,
  ChevronDropdown,
  ErrorDisplay,
  HeadlessTable,
  Loader,
  Modal,
  NoData,
  PageTitle,
  ReportSummaryStatusBadge,
  ReportVersionStatusBadge,
  StatusIndicator,
  Tooltip,
  TooltipIndicator,
} from "@/shared/components";

import type {
  ReportSummary,
  UiFileUploadErrorResponse,
  UiReportVersion,
} from "@/generated/ui-api";
import * as sdk from "@/generated/ui-api";
import { formatLt, formatLtDateTime } from "@/utils/dateUtils";
import { generateUuid } from "@/utils/uuid";
import { useBreadcrumb } from "@/shared/hooks/useBreadcrumb";
import { useToast } from "@/shared/hooks/useToast";
import {
  useReportStatuses,
  useReportSummaryStatuses,
  useTradeDirections,
} from "@/hooks/useClassifierQueries";
import { CopyIcon } from "@/assets/icons";
import { useAuth } from "@/auth";

type VersionTableRow = UiReportVersion & {
  isProcessing?: boolean;
  submissionId?: number;
  submissionType?: string;
};

const saveFile = (blob: Blob, fileName: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const decodeRfc5987Value = (value: string): string | null => {
  try {
    return decodeURIComponent(value.trim().replace(/^["']|["']$/g, ""));
  } catch {
    return null;
  }
};

const getFileNameFromResponse = (
  response: { response: Response },
  fallbackName: string
): string => {
  const header = response.response.headers.get("content-disposition");
  if (!header) {
    return fallbackName;
  }

  const extended = header.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
  if (extended?.[1]) {
    const decoded = decodeRfc5987Value(extended[1]);
    if (decoded) {
      return decoded;
    }
  }

  const plain = header.match(/filename="?([^";]+)"?/i);
  if (plain?.[1]) {
    return plain[1].trim() || fallbackName;
  }

  return fallbackName;
};

export default function ReportPage() {
  const { t } = useTranslation();
  const { reportId: reportIdString } = useParams<{ reportId: string }>();
  const reportId = reportIdString ? Number(reportIdString) : undefined;
  const navigate = useNavigate();
  const { state } = useLocation();
  const stateSummary = state?.summary as ReportSummary | undefined;
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [processingSubmissionId, setProcessingSubmissionId] = useState<
    number | null
  >(null);
  const handledSubmissionIdsRef = useRef<Set<number>>(new Set());
  const [showCreateConfirm, setShowCreateConfirm] = useState(false);
  const [showCopyConfirm, setShowCopyConfirm] = useState(false);
  const [selectedSourceVersionId, setSelectedSourceVersionId] = useState<
    number | undefined
  >(undefined);
  const [idempotencyKey, setIdempotencyKey] = useState<string | undefined>(
    undefined
  );
  const { representedEntity } = useAuth();
  const queryClient = useQueryClient();

  const formatUploadError = (error: unknown) => {
    const errObj = error as { body?: unknown } | null | undefined;
    const body = errObj?.body || error;

    if (
      body &&
      typeof body === "object" &&
      "errors" in body &&
      Array.isArray((body as UiFileUploadErrorResponse).errors)
    ) {
      const response = body as UiFileUploadErrorResponse;
      if (response.errors && response.errors.length > 0) {
        return response.errors
          .map(e => {
            if (e.message) {
              return e.message;
            }
            const params = e.params ? Object.assign({}, e.params) : {};
            const code = e.code?.trim();
            return t(`fileUploadErrors.${code}`, {
              defaultValue: code,
              ...params,
            });
          })
          .join("\n");
      }
      if (response.message) return response.message;
    }
    return handleApiError(error as Error, t);
  };

  const {
    data: reportData,
    isLoading: loading,
    error,
    isError,
  } = useReportById(reportId, !!representedEntity);

  // Fallback: if summary is not in state, fetch it based on reportData.reportingPeriod
  const { startDate, endDate } = useMemo(() => {
    if (reportData?.reportingPeriod) {
      const [year, month] = reportData.reportingPeriod.split("-").map(Number);
      const start = `${year}-${String(month).padStart(2, "0")}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const end = `${year}-${String(month).padStart(2, "0")}-${lastDay}`;
      return { startDate: start, endDate: end };
    }

    if (stateSummary?.reportingPeriod) {
      const { reportingYear, reportingMonth } = stateSummary.reportingPeriod;
      const start = `${reportingYear}-${String(reportingMonth).padStart(2, "0")}-01`;
      const lastDay = new Date(reportingYear, reportingMonth, 0).getDate();
      const end = `${reportingYear}-${String(reportingMonth).padStart(2, "0")}-${lastDay}`;
      return { startDate: start, endDate: end };
    }

    return { startDate: "", endDate: "" };
  }, [stateSummary, reportData?.reportingPeriod]);

  const { year, month } = useMemo(() => {
    if (reportData?.reportingPeriod) {
      const [y, m] = reportData.reportingPeriod.split("-").map(Number);
      return { year: y, month: m };
    }
    if (stateSummary?.reportingPeriod) {
      return {
        year: stateSummary.reportingPeriod.reportingYear,
        month: stateSummary.reportingPeriod.reportingMonth,
      };
    }
    return { year: undefined, month: undefined };
  }, [reportData?.reportingPeriod, stateSummary?.reportingPeriod]);

  const { data: submissionStatus } = useQuery({
    queryKey: ["uploadStatus", processingSubmissionId],
    queryFn: async () => {
      const response = await sdk.getReportSubmissionStatus({
        path: { submissionId: processingSubmissionId! },
        throwOnError: true,
      });
      return response.data;
    },
    enabled: processingSubmissionId !== null,
    refetchInterval: query =>
      query.state.data === undefined ||
      query.state.data?.status === "PERFORMING"
        ? 2000
        : false,
  });

  useEffect(() => {
    if (processingSubmissionId !== null) return;
    const inFlight = reportData?.processingSubmissions?.find(
      s =>
        s.submissionId !== undefined &&
        !handledSubmissionIdsRef.current.has(s.submissionId)
    );
    if (inFlight?.submissionId !== undefined) {
      setProcessingSubmissionId(inFlight.submissionId);
    }
  }, [reportData, processingSubmissionId]);

  useEffect(() => {
    if (processingSubmissionId === null || !submissionStatus) return;
    if (submissionStatus.status === "PERFORMING") return;

    handledSubmissionIdsRef.current.add(processingSubmissionId);

    if (submissionStatus.status === "SUCCESS") {
      showToast(
        t("reportPage.notifications.uploadProcessedSuccessfully"),
        "success"
      );
      void queryClient.invalidateQueries({ queryKey: reportKeys.all() });
      if (submissionStatus.intrastatReportId && !reportId) {
        navigate(`/report/${submissionStatus.intrastatReportId}`, {
          replace: true,
        });
      }
    } else if (submissionStatus.status === "ERROR") {
      showToast(
        submissionStatus.errorMessage ??
          t("reportPage.errors.uploadProcessingFailed"),
        "error"
      );
    }
    setProcessingSubmissionId(null);
  }, [
    submissionStatus,
    processingSubmissionId,
    reportId,
    queryClient,
    navigate,
    showToast,
    t,
  ]);

  const { data: summariesData } = useReportSummaries(
    startDate,
    endDate,
    !!representedEntity
  );

  const summary = useMemo(() => {
    if (stateSummary) return stateSummary;
    if (!summariesData) return undefined;

    const allSummaries = [
      ...(summariesData.reportSummariesImport ?? []),
      ...(summariesData.reportSummariesExport ?? []),
    ];
    return allSummaries.find(s => s.id === reportId);
  }, [stateSummary, summariesData, reportId]);

  const tradeDirection = useMemo(() => {
    if (reportData?.tradeDirection) return reportData.tradeDirection;
    if (summariesData) {
      if (summariesData.reportSummariesImport?.some(s => s.id === reportId))
        return "IMPORT";
      if (summariesData.reportSummariesExport?.some(s => s.id === reportId))
        return "EXPORT";
    }
    return state?.tradeDirection || "";
  }, [reportData, summariesData, reportId, state?.tradeDirection]);

  const businessEntityName =
    reportData?.businessEntityName ?? summariesData?.businessEntityName;
  const businessEntityVat =
    reportData?.businessEntityVat ?? summariesData?.businessEntityIdentifierVAT;

  const { mutate: createVersion, isPending: isCreatingVersion } =
    useCreateReportVersion();

  const { data: tradeDirections } = useTradeDirections();
  const { data: reportStatuses } = useReportStatuses();
  const { data: reportSummaryStatuses } = useReportSummaryStatuses();

  const getTradeDirectionLabel = useCallback(
    (key?: string) => {
      return tradeDirections?.find(d => d.key === key)?.label || key || "";
    },
    [tradeDirections]
  );

  const getReportStatusLabel = useCallback(
    (key?: string) => {
      return reportStatuses?.find(s => s.key === key)?.label || key || "";
    },
    [reportStatuses]
  );

  const getReportSummaryStatusLabel = useCallback(
    (key?: string) => {
      return (
        reportSummaryStatuses?.find(s => s.key === key)?.label || key || ""
      );
    },
    [reportSummaryStatuses]
  );

  const handleDownload = useCallback(
    async (versionId: number, fileType: "xml" | "xlsx") => {
      try {
        const downloadFn =
          fileType === "xml"
            ? sdk.downloadReportVersionXml
            : sdk.downloadReportVersionXlsx;

        const response = await downloadFn({
          path: { versionId },
          throwOnError: true,
        });

        const fileBlob = response.data;
        if (fileBlob instanceof Blob) {
          const fallbackName = `intrastat-report-${versionId}.${fileType}`;
          const fileName = getFileNameFromResponse(response, fallbackName);
          saveFile(fileBlob, fileName);
        } else {
          showToast(t("reportPage.errors.invalidFileResponse"), "error");
        }
      } catch (err) {
        showToast(handleApiError(err as Error, t), "error");
      }
    },
    [t, showToast]
  );

  const executeCopy = useCallback(() => {
    if (selectedSourceVersionId !== undefined) {
      createVersion(
        { sourceVersionId: selectedSourceVersionId, idempotencyKey },
        {
          onSuccess: data => {
            setShowCopyConfirm(false);
            setSelectedSourceVersionId(undefined);
            navigate(`/reports/versions/${data.data.version.id}/items`, {
              state: { itemsData: data.data },
            });
          },
          onError: err => {
            setShowCopyConfirm(false);
            setSelectedSourceVersionId(undefined);
            showToast(handleApiError(err as Error, t), "error");
          },
        }
      );
    }
  }, [
    createVersion,
    navigate,
    showToast,
    t,
    selectedSourceVersionId,
    idempotencyKey,
  ]);

  const handleCopy = useCallback((sourceVersionId: number) => {
    setSelectedSourceVersionId(sourceVersionId);
    setIdempotencyKey(generateUuid());
    setShowCopyConfirm(true);
  }, []);

  const executeCreate = useCallback(() => {
    if (year !== undefined && month !== undefined && tradeDirection) {
      createVersion(
        {
          reportingPeriod: { reportingYear: year, reportingMonth: month },
          tradeDirection,
          idempotencyKey,
        },
        {
          onSuccess: data => {
            setShowCreateConfirm(false);
            navigate(`/reports/versions/${data.data.version.id}/items`);
          },
          onError: err => {
            setShowCreateConfirm(false);
            showToast(handleApiError(err as Error, t), "error");
          },
        }
      );
    }
  }, [
    createVersion,
    navigate,
    showToast,
    t,
    year,
    month,
    tradeDirection,
    idempotencyKey,
  ]);

  const handleCreate = useCallback(() => {
    setIdempotencyKey(generateUuid());
    setShowCreateConfirm(true);
  }, []);

  const summaryColumns = useMemo<
    ColumnDef<ReportSummary & { tradeDirection: string }>[]
  >(
    () => [
      {
        accessorKey: "reportingPeriod",
        id: "reportingPeriod",
        header: t("summaryPage.table.reportingPeriod"),
        size: 100,
        cell: info => {
          const period = info.getValue() as ReportSummary["reportingPeriod"];
          if (!period) {
            return "";
          }
          return (
            <div className="flex justify-center items-center h-full">
              {`${period.reportingYear}-${String(
                period.reportingMonth
              ).padStart(2, "0")}`}
            </div>
          );
        },
      },
      {
        accessorKey: "tradeDirection",
        id: "tradeDirection",
        header: t("reportPage.table.tradeDirection"),
        size: 80,
        cell: info => (
          <div className="flex justify-center items-center h-full">
            {getTradeDirectionLabel(info.getValue() as string)}
          </div>
        ),
      },
      {
        accessorKey: "reportingObligation",
        id: "obligation",
        header: t("summaryPage.table.obligation"),
        size: 80,
        cell: info => {
          const hasObligation = info.getValue() as boolean;
          return (
            <div className="flex justify-center items-center h-full">
              {hasObligation ? (
                <Tooltip content={t("summaryPage.tooltip.reportingObligation")}>
                  <div className="flex items-center">
                    <StatusIndicator status="neutral" size="lg" />
                  </div>
                </Tooltip>
              ) : (
                <Tooltip
                  content={t("summaryPage.tooltip.noReportingObligation")}
                >
                  <div className="flex items-center">
                    <div className="w-4 h-4 border-2 border-gray-300 rounded-full" />
                  </div>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "reportIsLate",
        id: "isLate",
        header: t("summaryPage.table.isLate"),
        size: 80,
        cell: info => (
          <div className="flex justify-center items-center h-full">
            {(info.getValue() as boolean) && (
              <Tooltip
                content={t("summaryPage.tooltip.reportIsLate")}
                variant="error"
              >
                <div className="flex items-center">
                  <StatusIndicator status="error" size="lg" />
                </div>
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        accessorKey: "reportWorkUnfinished",
        id: "workUnfinished",
        header: t("summaryPage.table.workUnfinished"),
        size: 40,
        cell: info => (
          <div className="flex justify-center items-center h-full">
            {(info.getValue() as boolean) && (
              <TooltipIndicator
                content={t("summaryPage.tooltip.workUnfinished")}
                variant="info"
                placement="top"
                size={20}
              />
            )}
          </div>
        ),
      },
      {
        accessorKey: "reportSummaryStatus",
        id: "status",
        header: t("summaryPage.table.status"),
        size: 100,
        cell: info => {
          const status = info.getValue() as "FULFILLED" | "PENDING";
          const hasObligation = info.row.original.reportingObligation;

          const showBadge =
            status === "FULFILLED" || (status === "PENDING" && hasObligation);

          return showBadge ? (
            <div className="flex justify-center items-center h-full">
              <ReportSummaryStatusBadge status={status}>
                {getReportSummaryStatusLabel(status)}
              </ReportSummaryStatusBadge>
            </div>
          ) : (
            ""
          );
        },
      },
      {
        id: "registrationNumber",
        header: t("reportPage.reportInfo.registrationNumber"),
        size: 150,
        cell: () => (
          <div className="flex justify-center items-center h-full">
            {reportData?.registrationNumber}
          </div>
        ),
      },
      {
        id: "registrationDate",
        header: t("reportPage.reportInfo.registrationDate"),
        size: 150,
        cell: () => (
          <div className="flex justify-center items-center h-full">
            {formatLt(reportData?.registrationDate)}
          </div>
        ),
      },
      {
        id: "actions",
        header: "",
        size: 200,
        cell: () => (
          <div className="flex gap-2 justify-end items-center h-full">
            <Tooltip content={t("reportPage.tooltips.create")}>
              <Button variant="primary" size="md" onClick={handleCreate}>
                {t("reportPage.buttons.create")}
              </Button>
            </Tooltip>
            <Tooltip
              content={
                processingSubmissionId !== null
                  ? t("reportPage.tooltips.uploadProcessing")
                  : t("reportPage.tooltips.upload")
              }
            >
              <Button
                variant="primary"
                size="md"
                onClick={() => fileInputRef.current?.click()}
                loading={isUploading || processingSubmissionId !== null}
                disabled={processingSubmissionId !== null}
                className="whitespace-nowrap"
              >
                {t("reportPage.buttons.upload")}
              </Button>
            </Tooltip>
          </div>
        ),
      },
    ],
    [
      t,
      getReportSummaryStatusLabel,
      reportData,
      getTradeDirectionLabel,
      isUploading,
      processingSubmissionId,
      handleCreate,
    ]
  );

  const columns = useMemo<ColumnDef<VersionTableRow>[]>(
    () => [
      {
        accessorKey: "versionNumber",
        id: "versionNumber",
        header: t("reportPage.table.versionNumber"),
        size: 50,
        cell: info =>
          info.row.original.isProcessing ? (
            <span className="text-gray-500 italic">
              {t("reportPage.table.newVersion")}
            </span>
          ) : (
            (info.getValue() as number)
          ),
      },
      {
        accessorKey: "status",
        id: "status",
        size: 80,
        header: t("reportPage.table.status"),
        cell: info => {
          if (info.row.original.isProcessing) {
            return (
              <div className="flex justify-center items-center">
                <ReportVersionStatusBadge status="PREPARED">
                  {t("reportPage.table.processing")}
                </ReportVersionStatusBadge>
              </div>
            );
          }
          const status = info.getValue() as string;
          return status ? (
            <div className="flex justify-center items-center">
              <ReportVersionStatusBadge status={status}>
                {getReportStatusLabel(status)}
              </ReportVersionStatusBadge>
            </div>
          ) : (
            ""
          );
        },
      },
      {
        accessorKey: "versionDate",
        id: "versionDate",
        size: 80,
        header: t("reportPage.table.versionDate"),
        cell: info =>
          info.row.original.isProcessing ? (
            ""
          ) : (
            <div className="flex justify-center items-center">
              {formatLtDateTime(info.getValue() as string)}
            </div>
          ),
      },
      {
        accessorKey: "isActual",
        id: "isActual",
        header: t("reportPage.table.isActual"),
        size: 50,
        cell: info =>
          info.getValue() ? (
            <div className="flex justify-center items-center font-bold text-gray-900">
              {t("reportPage.table.yes")}
            </div>
          ) : (
            ""
          ),
      },
      {
        accessorKey: "isError",
        id: "isError",
        header: t("reportPage.table.isError"),
        size: 50,
        cell: info => (
          <div className="flex justify-center items-center">
            {(info.getValue() as boolean) && (
              <Tooltip content={t("reportPage.table.isError")} variant="error">
                <div className="flex items-center">
                  <StatusIndicator status="error" size="lg" />
                </div>
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        accessorKey: "isWarning",
        id: "isWarning",
        header: t("reportPage.table.isWarning"),
        size: 50,
        cell: info => (
          <div className="flex justify-center items-center">
            {(info.getValue() as boolean) && (
              <Tooltip content={t("reportPage.table.isWarning")}>
                <div className="flex items-center">
                  <StatusIndicator status="warning" size="lg" />
                </div>
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        id: "actions",
        header: "",
        size: 120,
        isPlaceholder: true,
        cell: info => {
          const version = info.row.original;
          if (version.isProcessing) {
            return "";
          }
          const restricted = version.actionsRestricted ?? false;
          const restrictedClass = restricted ? "pointer-events-none" : "";
          const isPrepared = version.status === "PREPARED";
          const copyButton = (
            <Button
              variant="secondary"
              size="md"
              disabled={restricted}
              onClick={() => {
                if (version.id && !restricted) {
                  handleCopy(version.id);
                }
              }}
              className={isPrepared ? "invisible" : restrictedClass}
            >
              <div className="flex items-center gap-2">
                <CopyIcon size={16} />
                {t("reportPage.table.copy")}
              </div>
            </Button>
          );
          return (
            <div className="flex gap-2 justify-end items-center">
              {isPrepared ? (
                copyButton
              ) : (
                <Tooltip
                  content={
                    restricted
                      ? t("reportPage.tooltips.actionsRestricted")
                      : t("reportPage.tooltips.copy")
                  }
                >
                  {copyButton}
                </Tooltip>
              )}
              <Tooltip
                content={
                  restricted
                    ? t("reportPage.tooltips.actionsRestricted")
                    : t("reportPage.tooltips.download")
                }
              >
                <ChevronDropdown
                  label={t("reportPage.table.download")}
                  variant="secondary"
                  size="md"
                  disabled={restricted}
                  className={restrictedClass}
                  options={[
                    {
                      label: "XML",
                      value: "xml",
                      onClick: () => {
                        if (version.id !== undefined) {
                          handleDownload(version.id, "xml");
                        }
                      },
                    },
                    {
                      label: "XLSX",
                      value: "xlsx",
                      onClick: () => {
                        if (version.id !== undefined) {
                          handleDownload(version.id, "xlsx");
                        }
                      },
                    },
                  ]}
                />
              </Tooltip>
              <Button
                variant="primary"
                size="md"
                onClick={() =>
                  navigate(`/reports/versions/${version.id}/items`)
                }
              >
                {t("reportPage.table.open")}
              </Button>
            </div>
          );
        },
      },
    ],
    [t, navigate, handleDownload, handleCopy, getReportStatusLabel]
  );

  const sortedVersions = useMemo(() => {
    return [...(reportData?.versions ?? [])].sort(
      (a, b) => (b.versionNumber ?? 0) - (a.versionNumber ?? 0)
    );
  }, [reportData?.versions]);

  const summaryTableData = useMemo(() => {
    return summary ? [{ ...summary, tradeDirection }] : [];
  }, [summary, tradeDirection]);

  const summaryTable = useReactTable({
    data: summaryTableData,
    columns: summaryColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const processingRows = useMemo<VersionTableRow[]>(() => {
    const fromReport = reportData?.processingSubmissions ?? [];
    const reportedIds = new Set(
      fromReport
        .map(s => s.submissionId)
        .filter((id): id is number => id !== undefined)
    );
    const includeLocal =
      processingSubmissionId !== null &&
      !reportedIds.has(processingSubmissionId);
    const rows: VersionTableRow[] = fromReport.map(submission => ({
      isProcessing: true,
      submissionId: submission.submissionId,
      submissionType: submission.type,
    }));
    if (includeLocal) {
      rows.push({
        isProcessing: true,
        submissionId: processingSubmissionId ?? undefined,
      });
    }
    return rows;
  }, [reportData?.processingSubmissions, processingSubmissionId]);

  const tableData = useMemo<VersionTableRow[]>(
    () => [...processingRows, ...sortedVersions],
    [processingRows, sortedVersions]
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const { setBreadcrumbs } = useBreadcrumb();

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const isXml = fileName.endsWith(".xml");
    const isXlsx = fileName.endsWith(".xlsx");

    if (!isXml && !isXlsx) {
      showToast(t("reportPage.errors.invalidFileType"), "error");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setIsUploading(true);

    try {
      const response = isXml
        ? await sdk.uploadReportSubmissionXml({
            query: { reportId, year, month, tradeDirection },
            body: { intrastatReportSubmissionXml: file },
            throwOnError: true,
          })
        : await sdk.uploadReportSubmissionXlsx({
            query: { reportId, year, month, tradeDirection },
            body: { intrastatReportSubmissionXlsx: file },
            throwOnError: true,
          });

      const submissionId = response.data?.submissionId;
      if (submissionId !== undefined) {
        setProcessingSubmissionId(submissionId);
        showToast(t("reportPage.notifications.uploadAccepted"), "success");
      }
    } catch (error) {
      showToast(formatUploadError(error), "error");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  useEffect(() => {
    if (reportData || summary) {
      let label = "";
      if (reportData) {
        label = `${getTradeDirectionLabel(
          reportData.tradeDirection
        )} ${reportData.reportingPeriod}`;
      } else if (summary?.reportingPeriod) {
        const period = `${summary.reportingPeriod.reportingYear}-${String(
          summary.reportingPeriod.reportingMonth
        ).padStart(2, "0")}`;
        label = `${getTradeDirectionLabel(tradeDirection)} ${period}`;
      }

      const breadcrumbs = [
        { label: t("breadcrumbs.reports"), path: "/reports/summaries" },
        {
          label,
        },
      ];
      setBreadcrumbs(breadcrumbs);
    }
  }, [
    reportData,
    summary,
    tradeDirection,
    t,
    setBreadcrumbs,
    getTradeDirectionLabel,
  ]);

  if (loading) {
    return <Loader />;
  }

  if (!representedEntity) {
    return <Loader />;
  }

  const hasFallback = !!(summary && (businessEntityName || businessEntityVat));

  if (isError && !hasFallback) {
    if (isNotFoundError(error)) {
      return <NoData message={t("summaryPage.businessEntityNotFound")} />;
    }
    return <ErrorDisplay message={handleApiError(error, t)} />;
  }

  if (!reportData && !hasFallback) {
    return <NoData />;
  }

  return (
    <div>
      <PageTitle>{t("reportPage.reportInfo.title")}</PageTitle>
      <div className="space-y-1 text-sm text-black mb-6">
        <div className="whitespace-nowrap">
          <span className="font-bold">
            {t("reportPage.reportInfo.vatPayerTitle")}:
          </span>{" "}
          {businessEntityName}
        </div>
        <div className="whitespace-nowrap">
          <span className="font-bold">
            {t("reportPage.reportInfo.vatPayerCode")}:
          </span>{" "}
          {businessEntityVat}
        </div>
      </div>

      <hr className="my-6 border-gray-300" />

      {summary && (
        <div className="mb-6">
          <HeadlessTable table={summaryTable} rowClassName={() => "h-16"} />
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".xml,.xlsx"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      <HeadlessTable
        table={table}
        emptyMessage={t("reportPage.table.noVersions")}
        emptyStateClassName="p-8 text-center text-gray-500 bg-gray-50 rounded-md border border-gray-200"
      />

      {showCreateConfirm && (
        <Modal
          show={showCreateConfirm}
          onHide={() => setShowCreateConfirm(false)}
          closable={false}
          footer={
            <div className="flex justify-center gap-4 w-full">
              <Button
                variant="secondary"
                size="xl"
                onClick={() => setShowCreateConfirm(false)}
                disabled={isCreatingVersion}
              >
                {t("common.no")}
              </Button>
              <Button
                variant="primary"
                size="xl"
                onClick={executeCreate}
                loading={isCreatingVersion}
              >
                {t("common.yes")}
              </Button>
            </div>
          }
        >
          <div className="py-4 text-xl font-bold text-center text-gray-900">
            {t("reportPage.confirmCreate")}
          </div>
        </Modal>
      )}

      {showCopyConfirm && (
        <Modal
          show={showCopyConfirm}
          onHide={() => setShowCopyConfirm(false)}
          closable={false}
          footer={
            <div className="flex justify-center gap-4 w-full">
              <Button
                variant="secondary"
                size="xl"
                onClick={() => setShowCopyConfirm(false)}
                disabled={isCreatingVersion}
              >
                {t("common.no")}
              </Button>
              <Button
                variant="primary"
                size="xl"
                onClick={executeCopy}
                loading={isCreatingVersion}
              >
                {t("common.yes")}
              </Button>
            </div>
          }
        >
          <div className="py-4 text-xl font-bold text-center text-gray-900">
            {t("reportPage.confirmCopy")}
          </div>
        </Modal>
      )}
    </div>
  );
}
